import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link, useOutletContext } from "react-router-dom"
import useExperiments from "../../hooks/useExperiments.js"
import useApi from "../../hooks/useApi.js"
import ScreenshotPreview from "../shared/ScreenshotPreview.jsx"
import TargetingModal from "./TargetingModal.jsx"

const GEN_POLL_INTERVAL_MS = 3000
const GEN_TIMEOUT_MS = 90000

// Shop::Page.page_types — mirrored on the client so we don't need a round
// trip just to render the dropdown. Must stay in sync with the enum in
// app/models/shop/page.rb. "other" is omitted because the engine never
// targets it (anti-patterns deny list).
const PAGE_TYPES = [ "landing", "product", "category", "cart", "checkout", "thank_you" ]

function valuePreview(value) {
  if (value == null) return "—"
  if (typeof value === "string") return value
  try { return JSON.stringify(value) } catch { return String(value) }
}

function DomChangesList({ t, changes }) {
  if (!changes || changes.length === 0) {
    return (
      <p className="text-[13px] text-ink-tertiary" style={{ lineHeight: 1.55 }}>
        {t("dashboard.proposals.variant_no_changes")}
      </p>
    )
  }
  return (
    <ul className="space-y-2">
      {changes.map((c, i) => (
        <li
          key={i}
          className="font-mono text-[11px] text-ink-secondary border-l-2 border-rule pl-3 py-1"
          style={{ fontVariantNumeric: "tabular-nums" }}
        >
          <div className="text-ink-primary">{c.selector}</div>
          <div className="text-ink-faint mt-0.5">
            <span className="text-ai">{c.action}</span>
            {c.value !== undefined && <span> · {valuePreview(c.value)}</span>}
          </div>
        </li>
      ))}
    </ul>
  )
}

function ProposalCard({ t, index, proposal, onApprove, onDismiss, onRefresh, busy }) {
  const variants = (proposal.variants || []).filter((v) => !v.control)

  return (
    <article className="bg-surface-card border border-rule rounded-card">
      <div className="grid gap-6 px-6 py-[22px]" style={{ gridTemplateColumns: "36px minmax(0,1fr)" }}>
        <span
          className="font-mono text-[12px] text-ink-faint pt-[3px]"
          style={{ fontVariantNumeric: "tabular-nums", letterSpacing: "0.04em" }}
        >
          {String(index + 1).padStart(2, "0")}
        </span>

        <div className="space-y-5">
          <header>
            <h3 className="text-[16px] font-medium tracking-[-0.015em] text-ink-primary">
              {proposal.name}{proposal.name.endsWith(".") ? "" : "."}
            </h3>
            <p
              className="mt-2 font-mono text-[11px] text-ink-faint"
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              {t("dashboard.proposals.targets_meta", {
                selector: proposal.target_selector,
                // Unified shape-neutral field: single_page → page.path,
                // page_type → derived pattern (e.g. /product/*). Falls back
                // to page_path for old records where effective_page_pattern
                // wasn't serialized yet.
                path: proposal.page_pattern || proposal.page_path
              })}
            </p>
          </header>

          {/* M10/10d — visual preview ABOVE dom_changes so non-technical
              owners see the change first. dom_changes list stays below as
              the source-of-truth fallback. */}
          <ScreenshotPreview t={t} experiment={proposal} onRefresh={onRefresh} />

          {variants.map((v) => (
            <div key={v.id} className="space-y-3">
              <p
                className="font-mono text-[10px] text-ink-tertiary"
                style={{ letterSpacing: "0.10em" }}
              >
                {t("dashboard.proposals.variant_label", { name: v.name })}
              </p>

              {v.ai_reasoning && (
                <div>
                  <p
                    className="font-mono text-[10px] text-ai mb-1"
                    style={{ letterSpacing: "0.10em" }}
                  >
                    {t("dashboard.proposals.ai_reasoning_label")}
                  </p>
                  <p className="text-[13px] text-ink-secondary" style={{ lineHeight: 1.55 }}>
                    {v.ai_reasoning}
                  </p>
                </div>
              )}

              <div>
                <p
                  className="font-mono text-[10px] text-ink-tertiary mb-2"
                  style={{ letterSpacing: "0.10em" }}
                >
                  {t("dashboard.proposals.changes_label")}
                </p>
                <DomChangesList t={t} changes={v.dom_changes} />
              </div>
            </div>
          ))}

          <div className="flex flex-wrap gap-3 items-center pt-2">
            <button
              type="button"
              onClick={() => onApprove(proposal)}
              disabled={!!busy}
              className="bg-brand text-ink-on-dark rounded-button px-4 py-2 text-[13px] font-medium disabled:opacity-60"
            >
              {busy === "approve"
                ? t("dashboard.proposals.approving")
                : t("dashboard.proposals.approve")}
            </button>
            <button
              type="button"
              onClick={() => onDismiss(proposal)}
              disabled={!!busy}
              className="text-[13px] text-ink-tertiary hover:text-failure disabled:opacity-60"
            >
              {busy === "dismiss"
                ? t("dashboard.proposals.dismissing")
                : t("dashboard.proposals.dismiss")}
            </button>
            <Link
              to={`/experiments/${proposal.id}`}
              className="text-[13px] font-medium text-ai ml-auto"
            >
              {t("dashboard.proposals.view_details")}
            </Link>
          </div>
        </div>
      </div>
    </article>
  )
}

function GeneratedSummaryPanel({ t, result, status, onDismiss }) {
  const { total, byStatus } = result
  const running = byStatus.running || 0
  const pending = byStatus.pending_approval || 0
  const draft = byStatus.draft || 0

  // M9/9i — pull skipped opportunities out of the final status blob so
  // owners can see *why* opportunities didn't produce proposals. Pre-9i
  // this was logged server-side and invisible.
  const opportunities = (status && Array.isArray(status.opportunities)) ? status.opportunities : []
  const skipped = opportunities.filter((o) => o.result === "skipped")

  return (
    <section className="bg-surface-card border border-rule rounded-card px-6 py-6">
      <div className="flex items-start gap-4">
        <span className="inline-block w-2.5 h-2.5 rounded-full bg-winner-dot mt-[7px]" aria-hidden />
        <div className="flex-1 space-y-4">
          <p
            className="font-mono text-[10px] text-winner"
            style={{ letterSpacing: "0.15em" }}
          >
            {t("dashboard.proposals.generated_eyebrow")}
          </p>
          <h2
            className="text-[22px] font-medium text-ink-primary"
            style={{ letterSpacing: "-0.03em" }}
          >
            {total === 0
              ? t("dashboard.proposals.run_summary_zero_generated")
              : total === 1
                ? t("dashboard.proposals.generated_title_one")
                : t("dashboard.proposals.generated_title_other", { count: total })}
          </h2>
          {total > 0 && (
            <ul className="space-y-1 text-[13px] text-ink-secondary" style={{ lineHeight: 1.55 }}>
              {running > 0 && <li>{t("dashboard.proposals.generated_running", { count: running })}</li>}
              {pending > 0 && <li>{t("dashboard.proposals.generated_pending", { count: pending })}</li>}
              {draft > 0 && <li>{t("dashboard.proposals.generated_draft", { count: draft })}</li>}
            </ul>
          )}

          {skipped.length > 0 && (
            <section className="pt-2">
              <p
                className="font-mono text-[10px] text-ink-tertiary mb-2"
                style={{ letterSpacing: "0.10em" }}
              >
                {t("dashboard.proposals.skip_reason_heading").toUpperCase()}
              </p>
              <ul className="space-y-2">
                {skipped.map((opp, i) => (
                  <li key={i} className="border-l-2 border-rule pl-3 py-1">
                    <p
                      className="font-mono text-[11px] text-ink-primary"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    >
                      {opp.path}
                      {opp.playbook && (
                        <span className="text-ink-faint ml-2">· {opp.playbook}</span>
                      )}
                    </p>
                    <p className="text-[12px] text-ink-secondary mt-1" style={{ lineHeight: 1.5 }}>
                      {skipReasonCopy(t, opp.skip_reason)}
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <div className="flex items-center gap-4 pt-1">
            {total > 0 && (
              <Link
                to="/experiments"
                className="text-[13px] font-medium text-ai"
              >
                {t("dashboard.proposals.generated_view_all")}
              </Link>
            )}
            <button
              type="button"
              onClick={onDismiss}
              className="text-[13px] text-ink-tertiary hover:text-ink-primary"
            >
              {t("dashboard.proposals.generated_dismiss")}
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}

// M9/9i — terminal "run failed" state from GenerateExperimentsJob's
// rescue block. Shows the truncated error so ops can see it at a glance;
// dismiss clears the blob so the next Generate starts clean.
function GenerateFailedPanel({ t, status, onDismiss }) {
  return (
    <section
      className="rounded-card border p-6 space-y-3"
      style={{ background: "#FEF2F2", borderColor: "#FCA5A5" }}
    >
      <p
        className="font-mono text-[10px] text-ink-tertiary"
        style={{ letterSpacing: "0.15em" }}
      >
        § AI · FAILED
      </p>
      <h2
        className="text-[22px] font-medium text-ink-primary"
        style={{ letterSpacing: "-0.03em" }}
      >
        {t("dashboard.proposals.run_failed_heading")}
      </h2>
      <p className="text-[13px] text-ink-primary" style={{ lineHeight: 1.55 }}>
        {t("dashboard.proposals.run_failed_body")}
      </p>
      {status?.error && (
        <pre
          className="bg-surface-card border border-rule rounded-card px-3 py-2 text-[12px] text-ink-secondary overflow-x-auto font-mono whitespace-pre-wrap"
          style={{ lineHeight: 1.5 }}
        >
          {status.error}
        </pre>
      )}
      <div className="pt-1">
        <button
          type="button"
          onClick={onDismiss}
          className="text-[13px] text-ink-tertiary hover:text-ink-primary"
        >
          {t("dashboard.proposals.generated_dismiss")}
        </button>
      </div>
    </section>
  )
}

// M9/9i — per-opportunity breadcrumbs streamed from Cro::ProgressWriter
// via Shop::Setting#generate_status. Each row shows path + playbook +
// current phase (fetching → calling_ai → validating → persisting → done/skipped).
// When a row is skipped, the skip_reason surfaces inline so the owner can
// see why a proposal wasn't produced (pre-9i these failures were silent).
const PHASE_ORDER = [ "fetching", "calling_ai", "validating", "persisting" ]

function phaseLabel(t, phase) {
  return t(`dashboard.proposals.generating_phase_${phase}`, {
    defaultValue: phase
  })
}

// Skip reasons come off the orchestrator with two shapes:
//   "validator:selector_not_found"   → look up skip_reason_validator_selector_not_found
//   "nav_region_selector"            → look up skip_reason_nav_region_selector
function skipReasonCopy(t, reason) {
  if (!reason) return null
  const key = reason.startsWith("validator:")
    ? `skip_reason_validator_${reason.slice("validator:".length)}`
    : `skip_reason_${reason}`
  const translated = t(`dashboard.proposals.${key}`, {
    defaultValue: t("dashboard.proposals.skip_reason_unknown", { reason })
  })
  return translated
}

function PhaseStepper({ t, current, skipped }) {
  if (skipped) return null
  return (
    <ol className="flex flex-wrap items-center gap-2 mt-2">
      {PHASE_ORDER.map((p) => {
        const activeIdx = PHASE_ORDER.indexOf(current)
        const myIdx = PHASE_ORDER.indexOf(p)
        const done = activeIdx > myIdx || current === "done"
        const active = current === p
        const color = done ? "text-winner" : active ? "text-ai" : "text-ink-faint"
        return (
          <li
            key={p}
            className={`font-mono text-[10px] ${color}`}
            style={{ letterSpacing: "0.08em" }}
          >
            {done ? "✓" : active ? "●" : "○"} {phaseLabel(t, p)}
          </li>
        )
      })}
    </ol>
  )
}

function OpportunityRow({ t, opp }) {
  const skipped = opp.result === "skipped"
  const done = opp.result === "done"
  const dotColor = skipped ? "#94A3B8" : done ? "#047857" : "#0F766E"
  const rowOpacity = skipped ? 0.75 : 1

  return (
    <li
      className="border-t border-rule py-3"
      style={{ opacity: rowOpacity }}
    >
      <div className="flex items-start gap-3">
        <span
          className="inline-block w-2 h-2 rounded-full mt-[7px]"
          style={{ background: dotColor }}
          aria-hidden
        />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span
              className="font-mono text-[12px] text-ink-primary truncate"
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              {t("dashboard.proposals.generating_opportunity_label", { path: opp.path || "—" })}
            </span>
            {opp.playbook ? (
              <span
                className="font-mono text-[10px] text-ai"
                style={{ letterSpacing: "0.10em" }}
              >
                {t("dashboard.proposals.generating_opportunity_playbook", { playbook: opp.playbook.toUpperCase() })}
              </span>
            ) : (
              <span
                className="font-mono text-[10px] text-ink-faint"
                style={{ letterSpacing: "0.10em" }}
              >
                {t("dashboard.proposals.generating_opportunity_no_playbook")}
              </span>
            )}
          </div>
          {!skipped && !done && <PhaseStepper t={t} current={opp.phase} />}
          {skipped && (
            <p className="mt-1 text-[12px] text-ink-secondary" style={{ lineHeight: 1.5 }}>
              <span
                className="font-mono text-[10px] text-ink-tertiary mr-2"
                style={{ letterSpacing: "0.10em" }}
              >
                {t("dashboard.proposals.generating_phase_skipped").toUpperCase()}
              </span>
              {skipReasonCopy(t, opp.skip_reason)}
            </p>
          )}
          {done && opp.experiment_id && (
            <p className="mt-1 font-mono text-[11px] text-winner" style={{ letterSpacing: "0.05em" }}>
              ✓ {t("dashboard.proposals.generating_phase_done")} · #{opp.experiment_id}
            </p>
          )}
        </div>
      </div>
    </li>
  )
}

function GeneratingPanel({ t, elapsed, status }) {
  const seconds = Math.floor(elapsed / 1000)
  const opportunities = (status && Array.isArray(status.opportunities)) ? status.opportunities : []
  const stepKey = status?.step && status.step !== "processing" && status.step !== "starting"
    ? `dashboard.proposals.generating_step_${status.step}`
    : null
  const stepSubhead = stepKey ? t(stepKey, { defaultValue: status.step }) : null

  return (
    <section className="bg-surface-card border border-rule rounded-card px-6 py-8">
      <div className="flex items-start gap-4">
        <span className="relative flex h-2.5 w-2.5 mt-[7px]">
          <span
            className="absolute inline-flex h-full w-full rounded-full bg-ai opacity-60"
            style={{ animation: "ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite" }}
          />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-ai" />
        </span>
        <div className="flex-1 min-w-0">
          <p
            className="font-mono text-[10px] text-ai mb-2"
            style={{ letterSpacing: "0.15em" }}
          >
            {t("dashboard.proposals.generating_eyebrow")}
          </p>
          <h2
            className="text-[22px] font-medium text-ink-primary mb-2"
            style={{ letterSpacing: "-0.03em" }}
          >
            {t("dashboard.proposals.generating_title")}
          </h2>
          <p className="text-sm text-ink-secondary max-w-xl" style={{ lineHeight: 1.55 }}>
            {t("dashboard.proposals.generating_lede")}
          </p>
          {stepSubhead && (
            <p
              className="mt-3 font-mono text-[11px] text-ink-tertiary"
              style={{ letterSpacing: "0.05em" }}
            >
              {stepSubhead}
            </p>
          )}
          <p
            className="mt-4 font-mono text-[11px] text-ink-tertiary"
            style={{ fontVariantNumeric: "tabular-nums", letterSpacing: "0.05em" }}
          >
            {t("dashboard.proposals.generating_elapsed", { seconds })}
          </p>

          {opportunities.length > 0 && (
            <ul className="mt-5">
              {opportunities.map((opp, i) => (
                <OpportunityRow key={i} t={t} opp={opp} />
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  )
}

// M9/9h — owner picks the scope of the generate run. Auto uses the M7.5d
// traffic-gated path; page_type + url route to cold_start mode so shops
// without traffic still get proposals. Disabling + inline remediation is
// driven by `missing` — the same list the backend returns on a 422, so
// the UX is consistent whether we gate client-side or server-side.
function GenerateControls({
  t, scope, setScope, pageType, setPageType, url, setUrl,
  onGenerate, generating, disabled, disabledReason
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <label
        className="font-mono text-[10px] text-ink-tertiary"
        style={{ letterSpacing: "0.10em" }}
      >
        {t("dashboard.proposals.generate_scope_label")}
      </label>
      <select
        value={scope}
        onChange={(e) => setScope(e.target.value)}
        disabled={generating}
        className="bg-surface-card border border-rule rounded-card px-3 py-2 text-[13px] text-ink-primary disabled:opacity-60"
      >
        <option value="auto">{t("dashboard.proposals.generate_scope_auto")}</option>
        <option value="page_type">{t("dashboard.proposals.generate_scope_page_type")}</option>
        <option value="url">{t("dashboard.proposals.generate_scope_url")}</option>
      </select>

      {scope === "page_type" && (
        <select
          value={pageType}
          onChange={(e) => setPageType(e.target.value)}
          disabled={generating}
          className="bg-surface-card border border-rule rounded-card px-3 py-2 text-[13px] text-ink-primary disabled:opacity-60"
        >
          <option value="">{t("dashboard.proposals.generate_page_type_placeholder")}</option>
          {PAGE_TYPES.map((pt) => (
            <option key={pt} value={pt}>{t(`dashboard.experiments.page_type_${pt}`)}</option>
          ))}
        </select>
      )}

      {scope === "url" && (
        <input
          type="text"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          disabled={generating}
          placeholder={t("dashboard.proposals.generate_url_placeholder")}
          className="bg-surface-card border border-rule rounded-card px-3 py-2 text-[13px] font-mono text-ink-primary disabled:opacity-60 min-w-[220px]"
          style={{ fontVariantNumeric: "tabular-nums" }}
        />
      )}

      <button
        type="button"
        onClick={onGenerate}
        disabled={generating || disabled}
        title={disabledReason || undefined}
        className="bg-surface-card border border-rule rounded-button px-4 py-2 text-[13px] font-medium text-ink-primary disabled:opacity-60"
      >
        {generating ? t("dashboard.proposals.generating") : t("dashboard.proposals.generate")}
      </button>
    </div>
  )
}

function MissingPreconditionsPanel({ t, missing, activeScope }) {
  if (!missing || missing.length === 0) return null

  // Filter out no_traffic when the user has already switched to targeted —
  // we don't want to nag about it when the chosen scope already sidesteps it.
  const relevant = missing.filter((code) => {
    if (code === "no_traffic" && activeScope !== "auto") return false
    return true
  })
  if (relevant.length === 0) return null

  return (
    <section className="bg-surface-card border border-rule rounded-card px-5 py-4 space-y-2">
      <p
        className="font-mono text-[10px] text-ink-tertiary"
        style={{ letterSpacing: "0.15em" }}
      >
        {t("dashboard.proposals.missing_eyebrow")}
      </p>
      <ul className="space-y-1 text-[13px] text-ink-secondary" style={{ lineHeight: 1.55 }}>
        {relevant.map((code) => (
          <li key={code} className="flex items-center gap-3">
            <span className="text-failure">·</span>
            <span>{t(`dashboard.proposals.missing_${code}`)}</span>
            {code === "no_conversion_rules" && (
              <Link to="/settings" className="text-[13px] font-medium text-ai">
                {t("dashboard.proposals.missing_fix_in_settings")}
              </Link>
            )}
            {code === "no_traffic" && (
              <span className="font-mono text-[11px] text-ink-faint">
                {t("dashboard.proposals.missing_no_traffic_hint")}
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}

export default function ProposalsPage({ t }) {
  const api = useApi()
  const {
    shop,
    shopState,
    metrics,
    refreshMetrics,
    refreshPendingProposals
  } = useOutletContext() || {}
  // useShop strips `profile` out of the shop object into a sibling key on
  // shopState. Read industry from shopState.profile, not shop.profile
  // (which is always undefined).
  const profile = shopState?.profile
  const { status, data: experiments, error, refresh } = useExperiments()

  const [busyId, setBusyId] = useState(null)
  const [generating, setGenerating] = useState(false)
  const [genElapsed, setGenElapsed] = useState(0)
  const [genTimedOut, setGenTimedOut] = useState(false)
  const [genResult, setGenResult] = useState(null)
  const [actionError, setActionError] = useState(null)

  // M9/9h — generation scope state
  const [scope, setScope] = useState("auto")
  const [pageType, setPageType] = useState("")
  const [urlInput, setUrlInput] = useState("")
  const [serverMissing, setServerMissing] = useState(null)

  // M9/9i — live breadcrumbs from Cro::ProgressWriter. `generateStatus`
  // holds the in-flight or most-recent run; we keep it around after
  // generating flips off so the owner can see skip reasons in the
  // summary block until they dismiss.
  const [generateStatus, setGenerateStatus] = useState(null)

  // M2 Phase 2 — AI targeting modal. Only shown when scope === "auto".
  // Advanced modes (page_type / url) skip the brain entirely and hit
  // /experiments/generate directly as they did pre-phase-2.
  const [targetingOpen, setTargetingOpen] = useState(false)

  const priorIdsRef = useRef(new Set())

  const proposals = experiments.filter((e) => e.status === "pending_approval")

  // Missing preconditions derived from the same shop+metrics data the
  // backend inspects. Targeted scopes (page_type / url) skip the no_traffic
  // gate since cold_start mode is the whole answer to "no traffic yet."
  const clientMissing = useMemo(() => {
    const missing = []
    if (!profile?.industry) missing.push("profile_not_analyzed")
    if (!(shop?.conversion_rules || []).some((r) => r.active)) missing.push("no_conversion_rules")
    if (scope === "auto" && metrics && !metrics.has_traffic_for_auto_generate) {
      missing.push("no_traffic")
    }
    return missing
  }, [ profile, shop, metrics, scope ])

  const targetedInputInvalid = (scope === "page_type" && !pageType) ||
                               (scope === "url" && !urlInput.trim())
  const generateDisabled = clientMissing.length > 0 || targetedInputInvalid

  const disabledReason = targetedInputInvalid
    ? (scope === "page_type"
        ? t("dashboard.proposals.generate_disabled_no_page_type")
        : t("dashboard.proposals.generate_disabled_no_url"))
    : (clientMissing.length > 0 ? t("dashboard.proposals.generate_disabled_preconditions") : null)

  const refreshAll = useCallback(async () => {
    await refresh()
    if (refreshPendingProposals) await refreshPendingProposals()
  }, [ refresh, refreshPendingProposals ])

  const onApprove = async (p) => {
    setBusyId({ id: p.id, op: "approve" })
    setActionError(null)
    try {
      await api.post(`/api/v1/shop/experiments/${p.id}/approve`)
      await refreshAll()
    } catch (err) { setActionError(err.message) }
    finally { setBusyId(null) }
  }

  const onDismiss = async (p) => {
    setBusyId({ id: p.id, op: "dismiss" })
    setActionError(null)
    try {
      await api.post(`/api/v1/shop/experiments/${p.id}/archive`)
      await refreshAll()
    } catch (err) { setActionError(err.message) }
    finally { setBusyId(null) }
  }

  // Actual kickoff of the orchestrator's /experiments/generate endpoint.
  // Used by both the Auto path (after modal approval) and the advanced
  // paths (page_type / url picked manually in the Advanced controls).
  const kickoffGenerate = async ({ pageTypeParam, urlParam } = {}) => {
    priorIdsRef.current = new Set(experiments.map((e) => e.id))
    setGenTimedOut(false)
    setActionError(null)
    setServerMissing(null)
    setGenResult(null)
    setGenerateStatus(null)
    setGenElapsed(0)
    setGenerating(true)
    const params = {}
    if (pageTypeParam) params.page_type = pageTypeParam
    if (urlParam) params.url = urlParam
    try {
      await api.post("/api/v1/shop/experiments/generate", params)
    } catch (err) {
      // Backend 422 with structured missing[] is the defense-in-depth
      // mirror of the client gate. Surface the list inline so the user
      // sees exactly the same remediation copy either way.
      const payload = err.payload || {}
      if (Array.isArray(payload.missing)) setServerMissing(payload.missing)
      setActionError(err.message)
      setGenerating(false)
      // Refresh metrics in case server state drifted (e.g., traffic
      // arrived mid-click) — next render may re-enable the button.
      if (refreshMetrics) refreshMetrics()
    }
  }

  const onGenerate = async () => {
    // Auto → AI targeting modal first. Advanced picks (page_type / url)
    // skip the brain as agreed in the phase-2 design conversation.
    if (scope === "auto") {
      setActionError(null)
      setServerMissing(null)
      setTargetingOpen(true)
      return
    }
    if (scope === "page_type") {
      await kickoffGenerate({ pageTypeParam: pageType })
    } else if (scope === "url") {
      await kickoffGenerate({ urlParam: urlInput.trim() })
    }
  }

  // Called by TargetingModal when the owner approves the AI's pick.
  // The approved decision carries scope + page_type|url; we hand it to
  // the existing orchestrator endpoint exactly as if the owner had
  // picked that surface manually in the Advanced controls.
  const onTargetingApproved = async (decision) => {
    setTargetingOpen(false)
    if (!decision) return
    if (decision.scope === "page_type" && decision.page_type) {
      await kickoffGenerate({ pageTypeParam: decision.page_type })
    } else if (decision.scope === "single_page" && decision.url) {
      await kickoffGenerate({ urlParam: decision.url })
    }
  }

  // M9/9i — dismiss the finished/failed run panel. Clears the server
  // state too so the next refresh + fresh mount won't resurrect it.
  const onDismissRunSummary = async () => {
    try {
      await api.destroy("/api/v1/shop/experiments/generate_status")
    } catch { /* best-effort; local state still clears */ }
    setGenerateStatus(null)
    setGenResult(null)
  }

  // Resurrect the run panel if the user navigates to /proposals while a
  // job is in flight (mount-time check). Matches M7h's "never leave
  // the user staring at a blind spinner" site-wide contract.
  useEffect(() => {
    let cancelled = false
    api.get("/api/v1/shop/experiments/generate_status")
      .then((blob) => {
        if (cancelled || !blob || Object.keys(blob).length === 0) return
        setGenerateStatus(blob)
        if (blob.step === "finding_opportunities" || blob.step === "processing" || blob.step === "starting") {
          setGenerating(true)
          setGenElapsed(0)
        }
      })
      .catch(() => { /* first-render check; missing endpoint is fine */ })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!generating) return

    const startedAt = Date.now()

    const tick = setInterval(async () => {
      const elapsed = Date.now() - startedAt
      setGenElapsed(elapsed)
      if (elapsed >= GEN_TIMEOUT_MS) {
        setGenerating(false)
        setGenTimedOut(true)
        return
      }
      // Two parallel polls: experiments list (to detect new rows) +
      // generate_status (for live breadcrumbs + skip reasons).
      await Promise.allSettled([
        refresh(),
        api.get("/api/v1/shop/experiments/generate_status").then((blob) => {
          if (blob && Object.keys(blob).length > 0) setGenerateStatus(blob)
        }).catch(() => {})
      ])
    }, GEN_POLL_INTERVAL_MS)

    return () => clearInterval(tick)
  }, [ generating, refresh, api ])

  useEffect(() => {
    if (!generating) return
    const prior = priorIdsRef.current
    const newOnes = experiments.filter((e) => !prior.has(e.id))

    // Completion can arrive two ways: the server flips status.step to
    // "done"/"failed", OR we see new experiments in the list. Accept
    // either — whichever happens first.
    const statusComplete = generateStatus && (generateStatus.step === "done" || generateStatus.step === "failed")

    if (newOnes.length > 0 || statusComplete) {
      const counts = newOnes.reduce((acc, e) => {
        acc[e.status] = (acc[e.status] || 0) + 1
        return acc
      }, {})
      // Prefer the server-side summary count when the run has finalized.
      // React can update `experiments` (via useExperiments.refresh) and
      // `generateStatus` (via the status poll) on separate render ticks, so
      // `newOnes.length` sometimes reads 0 on the tick statusComplete flips
      // true — even when the server has already persisted the experiments.
      // `summary.generated` is the orchestrator's authoritative count;
      // fall back to newOnes.length when the server hasn't provided one yet.
      const serverGenerated = generateStatus?.summary?.generated
      const total = (statusComplete && typeof serverGenerated === "number")
        ? serverGenerated
        : newOnes.length
      setGenResult({ total, byStatus: counts })
      setGenerating(false)
      if (refreshPendingProposals) refreshPendingProposals()
    }
  }, [ experiments, generating, generateStatus, refreshPendingProposals ])

  const countCopy = proposals.length === 1
    ? t("dashboard.proposals.count_one")
    : t("dashboard.proposals.count_other", { count: proposals.length })

  return (
    <div className="space-y-10">
      {targetingOpen && (
        <TargetingModal
          t={t}
          onClose={() => setTargetingOpen(false)}
          onApproved={onTargetingApproved}
        />
      )}
      <header className="space-y-6">
        <div>
          <p
            className="font-mono text-[10px] text-ai mb-3"
            style={{ letterSpacing: "0.15em", fontVariantNumeric: "tabular-nums" }}
          >
            {t("dashboard.proposals.eyebrow")}
          </p>
          <div className="flex items-baseline justify-between gap-4 flex-wrap">
            <h1
              className="text-[28px] font-medium text-ink-primary"
              style={{ letterSpacing: "-0.035em" }}
            >
              {t("dashboard.proposals.title")}
            </h1>
            <GenerateControls
              t={t}
              scope={scope}
              setScope={setScope}
              pageType={pageType}
              setPageType={setPageType}
              url={urlInput}
              setUrl={setUrlInput}
              onGenerate={onGenerate}
              generating={generating}
              disabled={generateDisabled}
              disabledReason={disabledReason}
            />
          </div>
          <p className="mt-3 text-sm text-ink-secondary max-w-2xl" style={{ lineHeight: 1.55 }}>
            {t("dashboard.proposals.lede")}
          </p>
          {!generating && status === "loaded" && proposals.length > 0 && (
            <p
              className="mt-4 font-mono text-[11px] text-ink-tertiary"
              style={{ letterSpacing: "0.05em", fontVariantNumeric: "tabular-nums" }}
            >
              {countCopy}
            </p>
          )}
          {actionError && <p className="mt-3 text-sm text-failure">{actionError}</p>}
          {genTimedOut && (
            <p className="mt-3 text-sm text-failure flex items-center gap-3">
              {t("dashboard.proposals.timed_out")}
              <button
                type="button"
                onClick={onGenerate}
                className="text-[13px] font-medium text-ai underline"
              >
                {t("dashboard.proposals.retry")}
              </button>
            </p>
          )}
        </div>

        <MissingPreconditionsPanel
          t={t}
          missing={serverMissing || clientMissing}
          activeScope={scope}
        />
      </header>

      {generating && <GeneratingPanel t={t} elapsed={genElapsed} status={generateStatus} />}
      {!generating && generateStatus?.step === "failed" && (
        <GenerateFailedPanel t={t} status={generateStatus} onDismiss={onDismissRunSummary} />
      )}
      {!generating && genResult && generateStatus?.step !== "failed" && (
        <GeneratedSummaryPanel
          t={t}
          result={genResult}
          status={generateStatus}
          onDismiss={onDismissRunSummary}
        />
      )}

      {!generating && status === "loading" && (
        <p className="text-sm text-ink-tertiary">{t("dashboard.proposals.loading")}</p>
      )}
      {status === "error" && (
        <p className="text-sm text-failure">{error || t("dashboard.proposals.error")}</p>
      )}

      {!generating && status === "loaded" && proposals.length === 0 && (
        <section className="bg-surface-card border border-rule rounded-card px-6 py-10 space-y-3">
          <h2 className="text-[18px] font-medium tracking-[-0.02em] text-ink-primary">
            {t("dashboard.proposals.empty_title")}
          </h2>
          <p className="text-sm text-ink-secondary max-w-xl" style={{ lineHeight: 1.55 }}>
            {t("dashboard.proposals.empty_lede")}
          </p>
        </section>
      )}

      {status === "loaded" && proposals.length > 0 && (
        <section className="space-y-4">
          {proposals.map((p, i) => (
            <ProposalCard
              key={p.id}
              t={t}
              index={i}
              proposal={p}
              onApprove={onApprove}
              onDismiss={onDismiss}
              onRefresh={refresh}
              busy={busyId?.id === p.id ? busyId.op : null}
            />
          ))}
        </section>
      )}
    </div>
  )
}
